import { useEffect, useState, useRef } from "react"
import { useSelector } from "react-redux"
import { Link, useNavigate } from "react-router-dom"
import moment from "moment"
import * as complaintApis from "../../utilities/apis/complaint"
import { getComplaintStatus } from "../../utilities/enumerations/constants"
import Layout from "../../components/Layout"
import Pagination from "../../components/Pagination"

const Complaints = () => {
  const navigate = useNavigate()
  const { token, permissions } = useSelector(state => state.user)
  const categoryRef = useRef(null)
  const statusRef = useRef(null)
  const mobileRef = useRef(null)
  const [complaints, setComplaints] = useState([])
  const [active, setActive] = useState(1)
  const [count, setCount] = useState(0)
  const [filters, setFilters] = useState({
    trash: false,
    offset: 0,
    limit: 10,
  })

  const handleSearch = () => {
    const where = {}

    if (categoryRef.current.value) where.category = categoryRef.current.value
    if (statusRef.current.value) where.status = statusRef.current.value
    if (mobileRef.current.value) where.mobile = mobileRef.current.value

    setFilters({ ...filters, offset: 0, ...where })
    setActive(1)
  }

  const handleReset = () => {
    setFilters({ limit: 10, offset: 0 })
    categoryRef.current.value = ''
    statusRef.current.value = ''
    mobileRef.current.value = ''
    setActive(1)
  }

  const handleTrash = () => {
    setFilters({ ...filters, trash: !filters.trash })
  }

  const handleDelete = async (id) => {
    try {
      if (!window.confirm("Are you sure you want to trash?")) return

      const response = await complaintApis.deleteComplaint(token, id)
      if (!response.status) throw new Error(response.message)

      setComplaints((previous) => {
        const complaints = previous.filter(x => x.id !== id)
        return [...complaints]
      })
    } catch (error) {
      alert(error.message)
    }
  }

  const handleRestore = async (id) => {
    try {
      if (!window.confirm("Are you sure you want to restore?")) return

      const response = await complaintApis.restoreComplaint(token, id)
      if (!response.status) throw new Error(response.message)

      getComplaints()
    } catch (error) {
      alert(error.message)
    }
  }

  const getComplaints = async () => {
    try {
      const query = new URLSearchParams(filters)
      const response = await complaintApis.getComplaints(token, query.toString())
      if (!response.status) throw new Error(response.message)

      setCount(response.count)
      setComplaints(response.complaints)
    } catch (error) {
      alert(error.message)
    }
  }

  const handlePageChange = (page, offset) => {
    setFilters({ ...filters, offset })
    setActive(page)
  }

  useEffect(() => {
    getComplaints()
  }, [])

  useEffect(() => {
    getComplaints(filters)
  }, [filters])

  return (
    <Layout
      header={true}
      footer={true}
    >
      <main className="py-3">
        <div className="w-75 mx-auto">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className="lead text-body-emphasis fw-bold m-0">Complaints ({complaints.length})</p>
          </div>

          <div className="border rounded-4 mb-3">
            <div className="p-3 border-bottom rounded-top-4 bg-secondary">
              <div className="row gx-2">
                <div className="col-md-2">
                  <select ref={categoryRef} className="form-select form-select-sm rounded-3">
                    <option value="">Category</option>
                    <option value="payment issue">Payment Issue</option>
                    <option value="packing issue">Packing Issue</option>
                    <option value="missing item">Missing Item</option>
                    <option value="product quality">Product Quality</option>
                    <option value="delivery issue">Delivery Issue</option>
                    <option value="miscellaneous">Miscellaneous</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <select ref={statusRef} className="form-select form-select-sm rounded-3">
                    <option value="">Status</option>
                    <option value="pending">Pending</option>
                    <option value="assigned">Assigned</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <input ref={mobileRef} type="text" className="form-control form-control-sm rounded-3" id="mobile" placeholder="Mobile #" required />
                </div>
                <div className="col-md-2">
                  <button type="button" className={`btn btn-sm btn-${filters.trash ? 'light' : 'outline'}-danger rounded-3`} onClick={handleTrash}>Trash</button>
                </div>
                <div className="col-md-4">
                  <div className="d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-sm btn-light-theme rounded-3" onClick={handleSearch}><i className="fas fa-search me-2"></i> Search</button>
                    <button type="reset" className="btn btn-sm btn-light-danger rounded-3" onClick={handleReset}><i className="fas fa-times me-2"></i> Clear</button>
                  </div>
                </div>
              </div>
            </div>

            <table className="table table-hover shadow-none m-0">
              <thead className="table-light">
                <tr>
                  <th scope="col" style={{ width: "20%" }} className="align-middle">Category</th>
                  <th scope="col" style={{ width: "20%" }} className="align-middle">Customer</th>
                  <th scope="col" style={{ width: "20%" }} className="align-middle">Rider</th>
                  <th scope="col" style={{ width: "10%" }} className="align-middle text-center">Status</th>
                  <th scope="col" style={{ width: "20%" }} className="align-middle">Datetime</th>
                  <th scope="col" style={{ width: "10%" }} className="align-middle text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {complaints.map((row) => {
                  return (
                    <tr key={row.id.toString()}>
                      <td className="align-middle text-capitalize">{row.category}</td>
                      <td className="align-middle">
                        <p className="m-0">{row.name || "N/A"}</p>
                        <p className="small text-muted m-0">{row.customer.mobile}</p>
                      </td>
                      <td className="align-middle">{row.driver && row.driver.name}</td>
                      <td className="align-middle text-center">{getComplaintStatus[row.status]}</td>
                      <td className="align-middle">{moment(row.created_at).format('DD-MM-YYYY HH:mm')}</td>
                      <td className="align-middle text-center">
                        <div className="d-flex gap-2 justify-content-center">
                          {permissions["READ_COMPLAINT"] && !filters.trash && <Link to={`/dashboard/complaint?id=${row.id}`}><i className="far fa-clipboard"></i></Link>}
                          {permissions["DELETE_COMPLAINT"] && !filters.trash && <a className="text-danger" onClick={() => handleDelete(row.id)}><i className="far fa-trash-alt"></i></a>}
                          {permissions["DELETE_COMPLAINT"] && filters.trash && <a className="text-success" onClick={() => handleRestore(row.id)}><i className="fas fa-trash-restore-alt"></i></a>}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            <nav aria-label="pagination">
              <Pagination
                currentPage={active}
                totalCount={count}
                pageSize={filters.limit}
                offset={filters.offset}
                siblingCount={3}
                onPageChange={handlePageChange}
              />
            </nav>
          </div>
        </div>
      </main>
    </Layout>
  )
}

export default Complaints
